import React, { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import Button from '../ui/Button';
import StatusBanner from '../ui/StatusBanner';
import { StatusPill } from '../ui/Status';
import DirectorModal from './DirectorModal';
import { useConfirm } from '../ui/ConfirmDialog';
import {
  notifyDirectorAdminToast,
  notifyDirectorDataUpdated
} from '../../utils/directorEvents';

const ALL_DIRECTOR_LOCATIONS = 'ALL';

const POLICY_TYPE_OPTIONS = [
  { value: 'blackout_period', label: 'Blackout Date / Range' },
  { value: 'max_managers_off', label: 'Max Managers Off' }
];

const STATUS_FILTER_OPTIONS = [
  { value: 'active', label: 'Active Only' },
  { value: 'inactive', label: 'Inactive Only' },
  { value: 'all', label: 'All Policies' }
];

const EMPTY_FORM = {
  id: '',
  locationId: '',
  policyType: 'blackout_period',
  name: '',
  description: '',
  startDate: '',
  endDate: '',
  timeBlock: 'FULL DAY',
  maxManagersOff: '',
  isActive: true
};

function formatPolicyType(policyType) {
  return POLICY_TYPE_OPTIONS.find(option => option.value === policyType)?.label || policyType;
}

function buildInitialForm(selectedLocationCode, locations, editingPolicy = null) {
  if (editingPolicy) {
    return {
      id: String(editingPolicy.id),
      locationId: String(editingPolicy.locationId),
      policyType: editingPolicy.policyType,
      name: editingPolicy.name || '',
      description: editingPolicy.description || '',
      startDate: editingPolicy.startDate || '',
      endDate: editingPolicy.endDate || '',
      timeBlock: editingPolicy.timeBlock || 'FULL DAY',
      maxManagersOff: editingPolicy.maxManagersOff ?? '',
      isActive: Boolean(editingPolicy.isActive)
    };
  }

  const defaultLocation = selectedLocationCode !== ALL_DIRECTOR_LOCATIONS
    ? locations.find(location => location.isActive && location.code === selectedLocationCode)
    : locations.find(location => location.isActive);

  return {
    ...EMPTY_FORM,
    locationId: defaultLocation ? String(defaultLocation.id) : ''
  };
}

function DirectorPolicyAdminPanel({ policies, locations, selectedLocationCode, locationLabel }) {
  const confirm = useConfirm();
  const [editingPolicyId, setEditingPolicyId] = useState('');
  const [policyTypeFilter, setPolicyTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [form, setForm] = useState(() => buildInitialForm(selectedLocationCode, locations));
  const [criticalEditConfirmed, setCriticalEditConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [banner, setBanner] = useState('');

  useEffect(() => {
    const editingPolicy = policies.find(policy => String(policy.id) === String(editingPolicyId)) || null;
    setForm(buildInitialForm(selectedLocationCode, locations, editingPolicy));
    setCriticalEditConfirmed(false);
  }, [editingPolicyId, locations, policies, selectedLocationCode]);

  const filteredPolicies = useMemo(() => {
    return policies.filter(policy => {
      if (policyTypeFilter !== 'all' && policy.policyType !== policyTypeFilter) {
        return false;
      }

      if (statusFilter === 'active' && !policy.isActive) {
        return false;
      }

      if (statusFilter === 'inactive' && policy.isActive) {
        return false;
      }

      return true;
    });
  }, [policies, policyTypeFilter, statusFilter]);

  const editingPolicy = useMemo(
    () => policies.find(policy => String(policy.id) === String(editingPolicyId)) || null,
    [editingPolicyId, policies]
  );

  const scopedLocations = useMemo(
    () => (selectedLocationCode !== ALL_DIRECTOR_LOCATIONS
      ? locations.filter(location => location.isActive && location.code === selectedLocationCode)
      : locations.filter(location => location.isActive)),
    [locations, selectedLocationCode]
  );

  const hasCriticalEditChanges = useMemo(() => {
    if (!editingPolicy) {
      return false;
    }

    return (
      form.startDate !== (editingPolicy.startDate || '')
      || form.endDate !== (editingPolicy.endDate || '')
      || form.timeBlock !== (editingPolicy.timeBlock || 'FULL DAY')
      || String(form.maxManagersOff ?? '') !== String(editingPolicy.maxManagersOff ?? '')
      || form.policyType !== editingPolicy.policyType
      || String(form.locationId || '') !== String(editingPolicy.locationId || '')
    );
  }, [editingPolicy, form.endDate, form.locationId, form.maxManagersOff, form.policyType, form.startDate, form.timeBlock]);

  const handleFormChange = (field, value) => {
    setForm(current => ({
      ...current,
      [field]: value
    }));
  };

  const resetForm = () => {
    setEditingPolicyId('');
    setForm(buildInitialForm(selectedLocationCode, locations));
    setCriticalEditConfirmed(false);
  };

  const closeModal = () => {
    resetForm();
    setIsModalOpen(false);
  };

  const openCreateModal = () => {
    resetForm();
    setErrorMessage('');
    setBanner('');
    setIsModalOpen(true);
  };

  const handleSavePolicy = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    setBanner('');

    try {
      const payload = {
        locationId: Number(form.locationId),
        policyType: form.policyType,
        name: form.name.trim(),
        description: form.description.trim(),
        startDate: form.startDate,
        endDate: form.endDate || null,
        timeBlock: form.timeBlock,
        maxManagersOff: form.policyType === 'max_managers_off' && form.maxManagersOff !== ''
          ? Number(form.maxManagersOff)
          : null,
        isActive: form.isActive
      };

      const path = editingPolicyId ? `/api/policies/${editingPolicyId}` : '/api/policies';
      const method = editingPolicyId ? 'PATCH' : 'POST';
      const savedPolicy = await apiRequest(path, {
        method,
        body: JSON.stringify(payload)
      });

      const successMessage = editingPolicyId
        ? `Policy ${savedPolicy.name} updated.`
        : `Policy ${savedPolicy.name} created.`;

      setBanner(successMessage);
      notifyDirectorAdminToast(successMessage);
      notifyDirectorDataUpdated('Director policy data refreshed.');
      closeModal();
    } catch (error) {
      const nextMessage = error.payload?.code === 'POLICY_IN_USE'
        ? `${error.payload?.error || error.message} Safe edits: ${(error.payload?.safeEditableFields || []).join(', ')}.`
        : (error.message || 'Unable to save policy.');
      setErrorMessage(nextMessage);
      notifyDirectorAdminToast(nextMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (policy) => {
    setErrorMessage('');
    setBanner('');

    const confirmed = await confirm(
      policy.isActive
        ? 'Deactivate this policy? This stops future enforcement but preserves past request history.'
        : 'Reactivate this policy for future requests?',
      policy.isActive
        ? { title: 'Deactivate policy', confirmLabel: 'Deactivate', tone: 'danger' }
        : { title: 'Reactivate policy', confirmLabel: 'Reactivate' }
    );

    if (!confirmed) {
      return;
    }

    try {
      const updatedPolicy = await apiRequest(`/api/policies/${policy.id}/deactivate`, {
        method: 'PATCH',
        body: JSON.stringify({
          isActive: !policy.isActive
        })
      });

      const message = updatedPolicy.isActive
        ? `Policy ${updatedPolicy.name} reactivated.`
        : `Policy ${updatedPolicy.name} deactivated. Existing request records were preserved.`;

      setBanner(message);
      notifyDirectorAdminToast(message);
      notifyDirectorDataUpdated('Director policy data refreshed.');
    } catch (error) {
      const nextMessage = error.message || 'Unable to update policy status.';
      setErrorMessage(nextMessage);
      notifyDirectorAdminToast(nextMessage, 'error');
    }
  };

  return (
    <div className="director-admin-stack">
      <StatusBanner kind="error" message={errorMessage} />
      <StatusBanner kind="success" message={!errorMessage ? banner : ''} />

      <div className="director-split-grid director-phase-two-grid">
        <section className="director-card">
          <div className="role-section-header">
            <h2>Policy Actions</h2>
            <span>{locationLabel}</span>
          </div>
          <p className="role-section-copy">
            Create blackout and coverage rules for the current scope.
          </p>
          <div className="director-form-actions">
            <Button onClick={openCreateModal}>
              Create Policy
            </Button>
          </div>
        </section>

        <section className="director-card director-note-card">
          <div className="role-section-header">
            <h2>Operational Notes</h2>
            <span>Audit Safe</span>
          </div>
          <ul className="director-bullet-list">
            <li>Deactivate policies instead of deleting them.</li>
            <li>Blackout and coverage rules remain date-window based.</li>
            <li>Request history stays intact when policies change later.</li>
          </ul>
        </section>
      </div>

      <section className="director-card">
        <div className="role-section-header">
          <h2>Policy Oversight</h2>
          <span>{filteredPolicies.length} shown</span>
        </div>

        <div className="director-toolbar">
          <div className="director-toolbar-group">
            <label htmlFor="policy-type-filter">Type</label>
            <select id="policy-type-filter" value={policyTypeFilter} onChange={(event) => setPolicyTypeFilter(event.target.value)}>
              <option value="all">All Policy Types</option>
              {POLICY_TYPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="director-toolbar-group">
            <label htmlFor="policy-status-filter">Status</label>
            <select id="policy-status-filter" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              {STATUS_FILTER_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="table-card">
          <table className="role-table director-policy-table">
            <thead>
              <tr>
                <th>Name</th>
                <th className="director-policy-table-col-type">Type</th>
                <th className="director-policy-table-col-station">Location</th>
                <th>Window</th>
                <th className="director-policy-table-col-time">Time Block</th>
                <th className="director-policy-table-col-max">Max Off</th>
                <th className="director-policy-table-col-status">Status</th>
                <th className="director-policy-table-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPolicies.map(policy => (
                <tr key={policy.id}>
                  <td>{policy.name}</td>
                  <td className="director-policy-table-col-type">{formatPolicyType(policy.policyType)}</td>
                  <td className="director-policy-table-col-station">{policy.locationCode}</td>
                  <td>{policy.startDate}{policy.endDate ? ` to ${policy.endDate}` : ''}</td>
                  <td className="director-policy-table-col-time">{policy.timeBlock || 'FULL DAY'}</td>
                  <td className="director-policy-table-col-max">{policy.maxManagersOff ?? 'N/A'}</td>
                  <td className="director-policy-table-col-status">
                    <StatusPill tone={policy.isActive ? 'active' : 'inactive'}>
                      {policy.isActive ? 'Active' : 'Inactive'}
                    </StatusPill>
                  </td>
                  <td className="director-policy-table-col-actions">
                    <div className="director-table-actions">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setEditingPolicyId(String(policy.id));
                          setErrorMessage('');
                          setBanner('');
                          setIsModalOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant={policy.isActive ? 'danger' : 'secondary'}
                        onClick={() => handleToggleActive(policy)}
                      >
                        {policy.isActive ? 'Deactivate' : 'Reactivate'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredPolicies.length === 0 && (
                <tr>
                  <td colSpan="8">No policies match the current filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {isModalOpen ? (
        <DirectorModal
          title={editingPolicy ? 'Edit Policy' : 'Create Policy'}
          badge={editingPolicy ? 'Update' : 'New'}
          onClose={closeModal}
        >
          <StatusBanner
            kind="warning"
            message={editingPolicy ? 'This will affect future requests. Past request history will not be rewritten.' : ''}
            className="role-inline-message"
          />

          <form className="director-form-grid" onSubmit={handleSavePolicy}>
            <label>
              Policy Type
              <select value={form.policyType} onChange={(event) => handleFormChange('policyType', event.target.value)}>
                {POLICY_TYPE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label>
              Location
              <select value={form.locationId} onChange={(event) => handleFormChange('locationId', event.target.value)} required>
                <option value="">Select location</option>
                {scopedLocations.map(location => (
                  <option key={location.id} value={location.id}>{location.code} - {location.name}</option>
                ))}
              </select>
            </label>

            <label className="director-form-span">
              Name
              <input
                value={form.name}
                onChange={(event) => handleFormChange('name', event.target.value)}
                placeholder="Holiday blackout, summer restriction, AM staffing cap..."
                required
              />
            </label>

            <label className="director-form-span">
              Description
              <textarea
                value={form.description}
                onChange={(event) => handleFormChange('description', event.target.value)}
                placeholder="Optional note for operational context."
              />
            </label>

            <label>
              Start Date
              <input type="date" value={form.startDate} onChange={(event) => handleFormChange('startDate', event.target.value)} required />
            </label>

            <label>
              End Date
              <input type="date" value={form.endDate} onChange={(event) => handleFormChange('endDate', event.target.value)} />
            </label>

            <label>
              Time Block
              <select value={form.timeBlock} onChange={(event) => handleFormChange('timeBlock', event.target.value)}>
                <option value="FULL DAY">Full Day</option>
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </label>

            <label>
              Max Managers Off
              <input
                type="number"
                min="0"
                value={form.maxManagersOff}
                onChange={(event) => handleFormChange('maxManagersOff', event.target.value)}
                disabled={form.policyType !== 'max_managers_off'}
                placeholder={form.policyType === 'max_managers_off' ? 'Required for coverage rule' : 'Not used for blackout rules'}
              />
            </label>

            <label className="director-checkbox">
              <input
                checked={form.isActive}
                onChange={(event) => handleFormChange('isActive', event.target.checked)}
                type="checkbox"
              />
              Active policy
            </label>

            {editingPolicy && hasCriticalEditChanges ? (
              <label className="director-checkbox director-form-span">
                <input
                  checked={criticalEditConfirmed}
                  onChange={(event) => setCriticalEditConfirmed(event.target.checked)}
                  type="checkbox"
                />
                I understand this will affect future requests.
              </label>
            ) : null}

            <div className="director-form-actions director-form-actions-spread director-form-span">
              <Button
                type="submit"
                disabled={editingPolicy && hasCriticalEditChanges && !criticalEditConfirmed}
                loading={isSubmitting}
                loadingLabel="Saving…"
              >
                {editingPolicy ? 'Save Changes' : 'Create Policy'}
              </Button>
              <Button size="sm" variant="secondary" onClick={closeModal}>
                Cancel
              </Button>
            </div>
          </form>
        </DirectorModal>
      ) : null}
    </div>
  );
}

export default DirectorPolicyAdminPanel;
